import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

export const supabaseConfigOk = Boolean(supabaseUrl && supabaseAnonKey);

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// W2 (open dataset): metriche quantitative sostituibili con gli aggregati
// della vista material_profiles. Le altre colonne del vecchio DB (fuel, land
// use...) sono qualitative e restano editoriali.
const OPEN_METRICS = [
  { key: 'GHG Emissions (kg CO2e/kg)', col: 'ghg' },
  { key: 'Water Consumption (L/kg)', col: 'water' },
  { key: 'Energy Use (MJ/kg)', col: 'energy' }
];

// I numeri della vista possono arrivare come number o string (numeric di
// Postgres). Arrotonda a una precisione da display senza zeri di coda.
const formatOpenValue = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '';
  const abs = Math.abs(num);
  const fixed = abs >= 100 ? num.toFixed(0) : abs >= 10 ? num.toFixed(1) : num.toFixed(2);
  return String(parseFloat(fixed));
};

// Innesta i numeri del dataset open nelle chiavi storiche: il valore diventa
// la mediana (i componenti estraggono il primo numero della stringa, quindi
// grafici e radar la usano senza modifiche) e si aggiungono le chiavi piatte
// "<campo> Range/Confidence/Sources" per dettaglio e Compare. Se la metrica
// non ha datapoint, resta il valore storico del vecchio DB.
const applyOpenDatasetNumbers = (mapped, item) => {
  OPEN_METRICS.forEach(({ key, col }) => {
    const median = item[`${col}_median`];
    if (median === null || median === undefined) return;
    mapped[key] = formatOpenValue(median);
    const min = item[`${col}_min`];
    const max = item[`${col}_max`];
    if (min !== null && min !== undefined && max !== null && max !== undefined
        && Number(min) !== Number(max)) {
      mapped[`${key} Range`] = `${formatOpenValue(min)}–${formatOpenValue(max)}`;
      mapped[`${key} Min`] = Number(min);
      mapped[`${key} Max`] = Number(max);
    }
    if (item[`${col}_confidence`]) {
      mapped[`${key} Confidence`] = item[`${col}_confidence`];
    }
    if (item[`${col}_n_sources`]) {
      mapped[`${key} Sources`] = item[`${col}_n_sources`];
    }
    if (Array.isArray(item[`${col}_sources`]) && item[`${col}_sources`].length > 0) {
      mapped[`${key} Source List`] = item[`${col}_sources`];
    }
  });
  return mapped;
};

// Map Supabase column names to App expected format
const mapSupabaseToAppFormat = (supabaseData) => {
  return supabaseData.map(item => {
    const mapped = {
    // Core identification
    'Material ID': item.material_id || '',
    'Material Name': item.material_name || '',
    'Category': item.category || '',

    // Sustainability metrics
    'Sustainability Rating': item.sustainability_rating || '',
    'Sustainability Score': item.sustainability_score || item.sustainability_rating || '',
    'Environmental_Sustainability': item.environmental_sustainability || '',

    // Environmental impact
    'GHG Emissions (kg CO2e/kg)': item.ghg_emissions || '',
    'Water Consumption (L/kg)': item.water_consumption || '',
    'Energy Use (MJ/kg)': item.energy_use || '',
    'Fuel Consumption (MJ/kg)': item.fuel_consumption || '',
    'Land Use': item.land_use || '',
    'Chemical Use Level': item.chemical_use_level || '',
    'Toxicity': item.toxicity || '',
    'Biodegradability': item.biodegradability || '',

    // Social and governance
    'Social Sustainability': item.social_sustainability || '',
    'Governance': item.governance || '',

    // Physical properties
    'Durability': item.durability || '',
    'Tensile Strength (MPa)': item.tensile_strength || '',
    'Abrasion Resistance': item.abrasion_resistance || '',
    'Chemical Resistance': item.chemical_resistance || '',
    'Moisture Absorption': item.moisture_absorption || '',
    'Temperature Resistance': item.temperature_resistance || '',
    'Elasticity': item.elasticity || '',
    'Dyeability': item.dyeability || '',
    'Comfort Level': item.comfort_level || '',

    // Economic
    'Cost Range ($/kg)': item.cost_range || '',
    'Cost Volatility': item.cost_volatility || '',

    // Applications and context
    'Primary Applications': item.primary_applications || '',
    'Main Challenges': item.main_challenges || '',
    'Key Opportunities': item.key_opportunities || '',

    // Data sources
    'Data Source 1': item.data_source_1 || '',
    'Data Source 1 Date': item.data_source_1_date || '',
    'Data Source 1 URL': item.data_source_1_url || '',
    'Data Source 2': item.data_source_2 || '',
    'Data Source 2 Date': item.data_source_2_date || '',
    'Data Source 2 URL': item.data_source_2_url || '',
    'Data Source 3': item.data_source_3 || '',
    'Data Source 3 Date': item.data_source_3_date || '',
    'Data Source 3 URL': item.data_source_3_url || ''
    };
    // Le righe di material_profiles si riconoscono da fiber_name:
    // solo lì vengono innestati i numeri del dataset open.
    return item.fiber_name ? applyOpenDatasetNumbers(mapped, item) : mapped;
  });
};

// Function to fetch materials from Supabase
export const fetchMaterialsFromSupabase = async (tableName = 'materials') => {
  try {
    const { data, error } = await supabase
      .from(tableName)
      .select('*');

    if (error) {
      console.error('Error fetching from Supabase:', error);
      throw error;
    }

    // Map Supabase data to app format
    const mappedData = mapSupabaseToAppFormat(data || []);
    return mappedData;
  } catch (error) {
    console.error('Supabase fetch error:', error);
    throw error;
  }
};

export const checkInviteCode = async (inviteCode) => {
  const trimmed = String(inviteCode || '').trim();
  if (!trimmed) {
    return false;
  }

  const { data, error } = await supabase.rpc('check_invite_code', {
    code: trimmed
  });

  if (error) {
    console.error('Invite code check failed:', error);
    throw error;
  }

  return data === true;
};
